// =====================================================
// Lizzi V2 — App root + React Router wiring
//
// Dos mundos que conviven:
//   · App real (rutas en la raíz)  -> requiere sesión Supabase (+ MFA si aplica)
//   · Demo con datos mock (/demo)  -> acceso abierto, datos en cliente
//
// Internamente las screens consumen un `route` string + `setRoute(name)`.
// El AppShell acepta un `base` ('' real, '/demo' demo) para construir URLs.
// =====================================================
import React, { useCallback, useEffect, useState } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
  useParams,
} from 'react-router-dom';

// Side-effect: makes window.MockData available for legacy reads
import './mock/index.js';

import {
  Sidebar,
  Topbar,
  CommandPalette,
  ChatDock,
  ToastHost, ConfirmHost,
} from './components.jsx';
import { NewBidModal, UploadModal, NotificationsPanel, AnalysisProgressOverlay } from './modals.jsx';

import { LoginScreen }       from './screens/login.jsx';
import { ResetPasswordScreen, MfaChallengeScreen, ChangePasswordScreen } from './screens/auth-extra.jsx';
import { DashboardScreen }   from './screens/dashboard.jsx';
import { BidsScreen }        from './screens/bids.jsx';
import { BidDetailScreen }   from './screens/bid-detail.jsx';
import { RadarScreen }        from './screens/radar.jsx';
import { FaroScreen }         from './screens/faro.jsx';
import { BidMasterDetail }    from './screens/bid-master.jsx';

import { AssistantsScreen }  from './screens/assistants.jsx';
import { TariffScreen }      from './screens/tariff.jsx';
import { ChatScreen }        from './screens/chat.jsx';
import { MemoryScreen }      from './screens/memory.jsx';
import { LibraryScreen }     from './screens/library.jsx';
import { CalendarScreen }    from './screens/calendar.jsx';
import { TeamScreen }        from './screens/team.jsx';
import { SettingsScreen }    from './screens/settings.jsx';
import { AdminLogsScreen }   from './screens/admin-logs.jsx';

import { AuthProvider, useAuth } from './auth/AuthContext.jsx';
import { DataProvider } from './data/DataProvider.jsx';

// ----- route-name -> path segment (sin base) -----
const ROUTE_TO_SEG = {
  dashboard:  '',
  radar:      'radar',
  faro:       'faro',
  bids:       'bids',
  bidDetail:  'bids',
  
  chat:       'chat',
  assistants: 'assistants',
  tariff:     'tariff',
  library:    'library',
  calendar:   'calendar',
  team:       'team',
  settings:   'settings',
  adminLogs:  'adminLogs',
};

function buildPath(base, name) {
  const seg = ROUTE_TO_SEG[name] ?? name;
  const p = `${base}/${seg}`.replace(/\/+$/, '');
  return p === '' ? '/' : p;
}

function pathToRoute(pathname, base) {
  let rest = pathname;
  if (base && rest.startsWith(base)) rest = rest.slice(base.length);
  rest = rest.replace(/^\//, '');
  if (rest === '') return 'dashboard';
  if (rest.startsWith('bids/')) return 'bidDetail';
  return rest.split('/')[0] || 'dashboard';
}

// =====================================================
// AppShell: sidebar + topbar + outlet (router-driven)
// `base` = '' (app real) | '/demo' (modo demo)
// =====================================================
function AppShell({ base = '', demoMode = false }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem('lizzi:theme') || 'dark'; } catch (e) { return 'dark'; }
  });
  const NARROW = 1180;
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => (typeof window !== 'undefined' ? window.innerWidth < NARROW : false)
  );

  // Colapsa/expande automáticamente el panel lateral segun el ancho de pantalla
  useEffect(() => {
    let narrow = window.innerWidth < NARROW;
    function onResize() {
      const isNarrow = window.innerWidth < NARROW;
      if (isNarrow !== narrow) {
        narrow = isNarrow;
        setSidebarCollapsed(isNarrow);
      }
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const [cmdkOpen,    setCmdkOpen]    = useState(false);
  const [chatOpen,    setChatOpen]    = useState(false);
  const [newBidOpen,  setNewBidOpen]  = useState(false);
  const [uploadOpen,  setUploadOpen]  = useState(false);
  const [notifOpen,   setNotifOpen]   = useState(false);
  const [deepLinkAssistant, setDeepLinkAssistant] = useState(null);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try { localStorage.setItem('lizzi:theme', theme); } catch (e) {}
  }, [theme]);

  const route = pathToRoute(location.pathname, base);
  // Licitación abierta (si la hay): da contexto al chat lateral.
  const currentBidId = route === 'bidDetail'
    ? decodeURIComponent((location.pathname.split('/bids/')[1] || '').split('/')[0]) || null
    : null;
  const sidebarRoute = route === 'bidDetail' ? 'bids' : route;

  // En el Chat el menú lateral se pliega solo para dar sitio a la
  // conversación; al salir vuelve a como estaba.
  const sidebarBeforeChat = React.useRef(null);
  const collapsedNow = React.useRef(sidebarCollapsed);
  collapsedNow.current = sidebarCollapsed;
  useEffect(() => {
    if (route === 'chat') {
      if (sidebarBeforeChat.current === null) {
        sidebarBeforeChat.current = collapsedNow.current;
        setSidebarCollapsed(true);
      }
    } else if (sidebarBeforeChat.current !== null) {
      const prev = sidebarBeforeChat.current;
      sidebarBeforeChat.current = null;
      setSidebarCollapsed(prev);
    }
  }, [route]);

  const setRoute = useCallback(
    (name) => {
      if (name !== 'assistants') setDeepLinkAssistant(null);
      navigate(buildPath(base, name));
    },
    [navigate, base]
  );

  const openBid = useCallback((id) => navigate(`${base}/bids/${id}`), [navigate, base]);

  useEffect(() => {
    function onOpenCmdk()      { setCmdkOpen(true); }
    function onOpenChat()      { setChatOpen(true); }
    function onOpenNewBid()    { setNewBidOpen(true); }
    function onOpenUpload()    { setUploadOpen(true); }
    function onOpenNotif()     { setNotifOpen(true); }
    function onToggleTheme()   { setTheme((t) => (t === 'dark' ? 'light' : 'dark')); }
    function onOpenBid(e)      { openBid(e.detail); }
    function onOpenAssistant(e) {
      setDeepLinkAssistant(e.detail);
      navigate(`${base}/assistants`);
    }
    function onGotoSettings() { navigate(buildPath(base, 'settings')); }
    // Abre una conversación concreta en la pantalla de Chat (desde el panel lateral).
    function onOpenChatThread(e) {
      try { if (e.detail) sessionStorage.setItem('lizzi:open-thread', e.detail); } catch (_) {}
      setChatOpen(false);
      navigate(buildPath(base, 'chat'));
      window.dispatchEvent(new CustomEvent('lizzi:select-thread', { detail: e.detail }));
    }
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault(); setCmdkOpen(true);
      } else if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'j') {
        e.preventDefault(); setChatOpen((c) => !c);
      } else if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'l') {
        e.preventDefault(); onToggleTheme();
      } else if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'b') {
        e.preventDefault(); setSidebarCollapsed((c) => !c);
      }
    }

    window.addEventListener('lizzi:open-cmdk',      onOpenCmdk);
    window.addEventListener('lizzi:open-chat',      onOpenChat);
    window.addEventListener('lizzi:open-new-bid',   onOpenNewBid);
    window.addEventListener('lizzi:open-upload',    onOpenUpload);
    window.addEventListener('lizzi:open-notif',     onOpenNotif);
    window.addEventListener('lizzi:toggle-theme',   onToggleTheme);
    window.addEventListener('lizzi:open-bid',       onOpenBid);
    window.addEventListener('lizzi:open-assistant', onOpenAssistant);
    window.addEventListener('lizzi:goto-settings', onGotoSettings);
    window.addEventListener('lizzi:open-chat-thread', onOpenChatThread);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('lizzi:open-cmdk',      onOpenCmdk);
      window.removeEventListener('lizzi:open-chat',      onOpenChat);
      window.removeEventListener('lizzi:open-new-bid',   onOpenNewBid);
      window.removeEventListener('lizzi:open-upload',    onOpenUpload);
      window.removeEventListener('lizzi:open-notif',     onOpenNotif);
      window.removeEventListener('lizzi:toggle-theme',   onToggleTheme);
      window.removeEventListener('lizzi:open-bid',       onOpenBid);
      window.removeEventListener('lizzi:open-assistant', onOpenAssistant);
      window.removeEventListener('lizzi:goto-settings', onGotoSettings);
      window.removeEventListener('lizzi:open-chat-thread', onOpenChatThread);
      window.removeEventListener('keydown', onKey);
    };
  }, [openBid, navigate, base]);

  return (
    <div className={`app ${sidebarCollapsed ? 'collapsed' : ''}`}>
      <Sidebar
        route={sidebarRoute}
        setRoute={setRoute}
        collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
      />
      <div className="main">
        <Topbar
          route={route}
          setRoute={setRoute}
          theme={theme}
          setTheme={setTheme}
          openChat={() => setChatOpen(true)}
        />
        <div className="viewport">
          <Routes>
            <Route path=""            element={<DashboardScreen setRoute={setRoute} openBid={openBid} />} />
            {demoMode && <Route path="radar" element={<RadarScreen openBid={openBid} />} />}
            <Route path="faro" element={<FaroScreen demoMode={demoMode} />} />
            <Route path="bids"        element={<BidsScreen openBid={openBid} />} />
            <Route path="bids/:bidId" element={<BidDetailRoute base={base} demoMode={demoMode} />} />
            
            
            <Route path="chat"        element={<ChatScreen />} />
            <Route path="memory"      element={<MemoryScreen />} />
            <Route path="assistants"  element={<AssistantsScreen deepLink={deepLinkAssistant} />} />
            <Route path="tariff"      element={<TariffScreen />} />
            <Route path="library"     element={<LibraryScreen />} />
            <Route path="calendar"    element={<CalendarScreen />} />
            <Route path="team"        element={<TeamScreen />} />
            <Route path="settings"    element={<SettingsScreen theme={theme} setTheme={setTheme} demoMode={demoMode} />} />
            <Route path="adminLogs"   element={<AdminLogsScreen />} />
            <Route path="*"           element={<Navigate to={base || '/'} replace />} />
          </Routes>
        </div>
      </div>

      <CommandPalette open={cmdkOpen}   onClose={() => setCmdkOpen(false)}   route={route} setRoute={setRoute} />
      <ChatDock       open={chatOpen}   onClose={() => setChatOpen(false)} bidId={currentBidId} />
      <NewBidModal    open={newBidOpen} onClose={() => setNewBidOpen(false)} />
      <UploadModal    open={uploadOpen} onClose={() => setUploadOpen(false)} />
      <NotificationsPanel open={notifOpen} onClose={() => setNotifOpen(false)} />
      <AnalysisProgressOverlay />
      <ToastHost />
      <ConfirmHost />
    </div>
  );
}

function BidDetailRoute({ base, demoMode = false }) {
  const { bidId } = useParams();
  const navigate = useNavigate();
  const props = {
    bidId,
    back: () => navigate(`${base}/bids`),
    openEditor: () => navigate(`${base}/memory`),
  };
  return demoMode ? <BidMasterDetail {...props} /> : <BidDetailScreen {...props} />;
}

// =====================================================
// Pantalla de carga mientras se resuelve la sesión
// =====================================================
function AuthLoading() {
  return (
    <div style={{height:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg-0)'}}>
      <span className="skeleton" style={{width:28, height:28, borderRadius:999}}></span>
    </div>
  );
}

// =====================================================
// Gate de la app real: requiere sesión y MFA resuelto
// =====================================================
function ProtectedApp() {
  const { loading, session, mfaPending, mustChangePassword, passwordRecovery } = useAuth();
  // La app real nunca es demo: limpiamos cualquier bandera residual.
  useEffect(() => {
    try { localStorage.removeItem('lizzi:demo'); } catch (e) {}
  }, []);
  if (loading) return <AuthLoading />;
  if (!session) return <Navigate to="/login" replace />;
  // Una sesión de recuperación solo sirve para fijar una contraseña nueva.
  if (passwordRecovery) return <Navigate to="/reset-password" replace />;
  if (mfaPending) return <Navigate to="/mfa" replace />;
  if (mustChangePassword) return <Navigate to="/change-password" replace />;
  return (
    <DataProvider demoMode={false}>
      <AppShell base="" demoMode={false} />
    </DataProvider>
  );
}

// =====================================================
// Modo demo: acceso abierto con datos mock
// =====================================================
function DemoApp() {
  useEffect(() => {
    try { localStorage.setItem('lizzi:demo', '1'); } catch (e) {}
    setTimeout(
      () => window.dispatchEvent(new CustomEvent('lizzi:toast', {
        detail: { text: 'Estás en modo demo · datos cargados en cliente.', icon: 'Sparkles' },
      })),
      400
    );
  }, []);
  return (
    <DataProvider demoMode={true}>
      <AppShell base="/demo" demoMode={true} />
    </DataProvider>
  );
}

// =====================================================
// Rutas públicas de auth (redirigen si ya hay sesión)
// =====================================================
function LoginRoute() {
  const { loading, session, mfaPending } = useAuth();
  if (loading) return <AuthLoading />;
  if (session && !mfaPending) return <Navigate to="/" replace />;
  if (session && mfaPending) return <Navigate to="/mfa" replace />;
  return (
    <>
      <LoginScreen />
      <ToastHost />
      <ConfirmHost />
    </>
  );
}

function MfaRoute() {
  const { loading, session } = useAuth();
  if (loading) return <AuthLoading />;
  if (!session) return <Navigate to="/login" replace />;
  return <MfaChallengeScreen />;
}

function ChangePasswordRoute() {
  const { loading, session, mfaPending, mustChangePassword } = useAuth();
  if (loading) return <AuthLoading />;
  if (!session) return <Navigate to="/login" replace />;
  if (mfaPending) return <Navigate to="/mfa" replace />;
  if (!mustChangePassword) return <Navigate to="/" replace />;
  return <ChangePasswordScreen />;
}

// =====================================================
// App root
// =====================================================
export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login"          element={<LoginRoute />} />
          <Route path="/reset-password" element={<ResetPasswordScreen />} />
          <Route path="/mfa"            element={<MfaRoute />} />
          <Route path="/change-password" element={<ChangePasswordRoute />} />
          <Route path="/demo/*"         element={<DemoApp />} />
          <Route path="/*"              element={<ProtectedApp />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}
